import React from 'react';
import { OrgComponentProps } from '../../typescriptHelpers';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import { useSelector } from 'react-redux';
import { selectAuthCredentials, selectOrg } from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import { runwayColors, tagStyle } from '../styles/globalStyles';
import { SSOConnectionState, SubscriptionFeatureType } from '../../types';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import * as API from '../../api';
import { globalMessage } from '../../utils/globalMessage';
import Text from 'antd/lib/typography/Text';
import Tag from 'antd/lib/tag';
import { SettingOutlined } from '@ant-design/icons';
import { DirectorySyncUserGroupsForGroups } from './DirectorySyncUserGroupsForGroups';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import styles from '../styles/settingsStyles';

export const SsoSamlSettings: React.FC<OrgComponentProps> = (props: OrgComponentProps) => {
    const org = useSelector((state: RootState) => selectOrg(state, props.orgId));
    const [generateSSOAdminPortalURLIsLoading, setGenerateSSOAdminPortalURLIsLoading] = React.useState(false);
    const [generateDirectorySyncAdminPortalURLIsLoading, setGenerateDirectorySyncAdminPortalURLIsLoading] =
        React.useState(false);
    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));
    const redirectToSSOAdminPortal = React.useCallback(() => {
        const redirectUrl = `${window.location.origin}${location.pathname}`;

        setGenerateSSOAdminPortalURLIsLoading(true);

        API.generateSSOAdminPortalRedirect(props.orgId, redirectUrl, authCredentials)
            .then((response) => {
                window.location.href = response.portalURL;
            })
            .catch((error) => {
                globalMessage.error(error.message);
            })
            .finally(() => {
                setGenerateSSOAdminPortalURLIsLoading(false);
            });
    }, [authCredentials]);
    const redirectToDirectorySyncAdminPortal = React.useCallback(() => {
        const redirectUrl = `${window.location.origin}${location.pathname}`;

        setGenerateDirectorySyncAdminPortalURLIsLoading(true);

        API.generateDirectorySyncAdminPortalRedirect(props.orgId, redirectUrl, authCredentials)
            .then((response) => {
                window.location.href = response.portalURL;
            })
            .catch((error) => {
                globalMessage.error(error.message);
            })
            .finally(() => {
                setGenerateDirectorySyncAdminPortalURLIsLoading(false);
            });
    }, [authCredentials]);
    const colorForConnectionState = (status: SSOConnectionState) => {
        switch (status) {
            case SSOConnectionState.Active:
                return runwayColors.statusGreen;
            case SSOConnectionState.Inactive:
                return runwayColors.statusYellow;
            default:
                return runwayColors.statusYellow;
        }
    };
    const textForConnectionState = (status: SSOConnectionState) => {
        switch (status) {
            case SSOConnectionState.Active:
                return 'active';
            case SSOConnectionState.Inactive:
                return 'inactive';
            default:
                return 'not connected';
        }
    };
    const colorForDirectoryState = (isActive: boolean) => {
        return isActive ? runwayColors.statusGreen : runwayColors.statusYellow;
    };
    const textForDirectoryState = (isActive: boolean) => {
        return isActive ? 'active' : 'inactive';
    };

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    {org.ssoSamlConfig.ssoSamlEnabled ?
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                marginBottom: 32,
                                width: '50%',
                                minWidth: 500,
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    marginBottom: 32,
                                    width: '50%',
                                    minWidth: 500,
                                }}
                            >
                                <Space align="center" size={12}>
                                    <Text style={styles.appSettingsSectionTitleStyle}>
                                        SSO/SAML connection{' '}
                                        {org.ssoSamlConfig.ssoConnection?.name &&
                                            `(${org.ssoSamlConfig.ssoConnection.name})`}
                                    </Text>
                                    <Tag
                                        style={tagStyle}
                                        color={colorForConnectionState(org.ssoSamlConfig.ssoConnection?.state)}
                                    >
                                        {textForConnectionState(org.ssoSamlConfig.ssoConnection?.state)}
                                    </Tag>
                                </Space>
                                <Space direction="vertical" style={{ marginTop: 16 }}>
                                    <Text type="secondary">
                                        The SSO/SAML admin portal is hosted by our SSO provider, WorkOS. You must have
                                        the IT Admin permission in Runway to access the portal.
                                    </Text>
                                    <Space direction="vertical" style={{ marginTop: 24 }}>
                                        <PermissionedActionButton
                                            actionType={RunwayUserAction.UAAccessSSOPortal}
                                            icon={<SettingOutlined style={{ color: runwayColors.info }} />}
                                            loading={generateSSOAdminPortalURLIsLoading}
                                            onClick={redirectToSSOAdminPortal}
                                        >
                                            SSO/SAML admin portal
                                        </PermissionedActionButton>
                                    </Space>
                                </Space>
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    marginBottom: 32,
                                    width: '50%',
                                    minWidth: 500,
                                }}
                            >
                                <Space align="center" size={12}>
                                    <Text style={styles.appSettingsSectionTitleStyle}>
                                        Directory Sync{' '}
                                        {org.ssoSamlConfig.ssoDirectory?.name &&
                                            `(${org.ssoSamlConfig.ssoDirectory.name})`}
                                    </Text>
                                    <Tag
                                        style={tagStyle}
                                        color={colorForDirectoryState(org.ssoSamlConfig.ssoDirectory?.isActive)}
                                    >
                                        {textForDirectoryState(org.ssoSamlConfig.ssoDirectory?.isActive)}
                                    </Tag>
                                </Space>
                                <Space direction="vertical" style={{ marginTop: 16 }}>
                                    <Text type="secondary">
                                        The Directory Sync admin portal is hosted by our SSO/SAML provider, WorkOS. You
                                        must have the IT Admin permission in Runway to access the portal.
                                    </Text>
                                    <Space direction="vertical" style={{ marginTop: 24 }}>
                                        <PermissionedActionButton
                                            actionType={RunwayUserAction.UAAccessDirectorySyncPortal}
                                            icon={<SettingOutlined style={{ color: runwayColors.info }} />}
                                            loading={generateDirectorySyncAdminPortalURLIsLoading}
                                            onClick={redirectToDirectorySyncAdminPortal}
                                        >
                                            Directory Sync admin portal
                                        </PermissionedActionButton>
                                    </Space>
                                </Space>
                            </div>
                            {org.ssoSamlConfig.ssoDirectory?.isActive && (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        marginBottom: 32,
                                        minWidth: 500,
                                    }}
                                >
                                    <Space align="center" size={12}>
                                        <Text style={styles.appSettingsSectionTitleStyle}>
                                            Groups to Runway user groups
                                        </Text>
                                    </Space>
                                    <div>
                                        <Space direction="vertical" style={{ marginTop: 16 }}>
                                            <DirectorySyncUserGroupsForGroups {...props} />
                                        </Space>
                                    </div>
                                </div>
                            )}
                        </div>
                    :   <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.SAML} requiresTooltip={true}>
                            <Typography.Text>SSO/SAML is not enabled for your organization.</Typography.Text>
                        </PlanRestrictedFeatureWrapper>
                    }
                </div>
            </div>
        </>
    );
};
